import { API_ENDPOINTS, DEFAULT_PAGE_SIZE, ENTITY_STATUS } from '@/constants';
import { apiRequest } from '@/services/client';
import { validateUserResponse } from '@/services/shared/contractValidation';
import { mapApiListResponse } from '@/services/shared/listResponse';
import type { ApiListResponse } from '@/types/api';
import type {
  User,
  UserListParams,
  UserListResult,
  UserPayload,
  UserRole,
} from '@/types/user';
import { normalizeImageSource } from '@/utils/imageSource';
import { normalizeUiPageFromParams } from '@/utils/pagination';

type ManageableRoleResponse =
  | UserRole[]
  | {
      id?: number;
      name?: UserRole;
    }[];

const normalizeManageableRoles = (
  response: ManageableRoleResponse,
): UserRole[] => {
  if (!Array.isArray(response)) {
    return [];
  }
  return response
    .map((item) => (typeof item === 'string' ? item : item?.name))
    .filter((role): role is UserRole => Boolean(role));
};

export const fetchManageableRoles = async (): Promise<UserRole[]> => {
  const response = await apiRequest<ManageableRoleResponse>(
    API_ENDPOINTS.USERS.MANAGEABLE_ROLES,
    {
      retry: { retries: 1 },
    },
  );

  return normalizeManageableRoles(response);
};

type UserApiItem = Omit<User, 'id' | 'role'> & {
  id?: number | string;
  user_id?: number | string;
  userId?: number | string;
  role?: unknown;
};

type UserListApiResponse = UserApiItem[] | ApiListResponse<UserApiItem>;

const normalizeRoleParam = (role?: UserRole | UserRole[]) => {
  if (!role) return undefined;
  return Array.isArray(role) ? role.join(',') : role;
};
const normalizeStatusParam = (status?: ENTITY_STATUS | ENTITY_STATUS[]) => {
  if (!status) {
    return undefined;
  }
  return Array.isArray(status) ? status.join(',') : status;
};

function normalizeUserProfileImages(user: User): User {
  const profile = user.profile;
  if (!profile) {
    return user;
  }

  const normalizedPhoto = normalizeImageSource(profile.photo);
  const normalizedThumbnail = normalizeImageSource(profile.thumbnail);

  if (
    normalizedPhoto === profile.photo &&
    normalizedThumbnail === profile.thumbnail
  ) {
    return user;
  }

  return {
    ...user,
    profile: {
      ...profile,
      photo: normalizedPhoto,
      thumbnail: normalizedThumbnail,
    },
  };
}

type GetCurrentUserOptions = {
  useGlobalErrorHandler?: boolean;
};

export const getCurrentUser = async (
  options: GetCurrentUserOptions = {},
): Promise<User> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.USERS.ME, {
    retry: { retries: 1 },
    useGlobalErrorHandler: options.useGlobalErrorHandler,
  });
  const user = validateUserResponse(response, {
    operation: 'get_current_user',
    endpoint: API_ENDPOINTS.USERS.ME,
  });
  return normalizeUserProfileImages(user);
};

const normalizeUser = (user: UserApiItem, index: number): User => {
  const apiId = user.id ?? user.user_id ?? user.userId;
  const parsedId =
    typeof apiId === 'string' ? Number.parseInt(apiId, 10) : apiId;
  const normalizedUser = {
    ...user,
    id: Number.isFinite(parsedId) ? (parsedId as number) : index + 1,
    role: typeof user.role === 'string' ? user.role : undefined,
  };

  const validatedUser = validateUserResponse(normalizedUser, {
    operation: 'fetch_users_list_item',
    endpoint: API_ENDPOINTS.USERS.LIST,
    itemIndex: index,
  });

  return normalizeUserProfileImages(validatedUser);
};

const mapUserListResponse = (
  response: UserListApiResponse,
  params: UserListParams,
): UserListResult => {
  return mapApiListResponse<UserApiItem, User>(response, params, {
    defaultPageSize: DEFAULT_PAGE_SIZE,
    mapItem: normalizeUser,
  });
};

export const fetchUsers = async (
  params: UserListParams = {},
): Promise<UserListResult> => {
  const normalizedPage = normalizeUiPageFromParams(params);

  const emailParam = params.email;

  const requestParams: Record<string, unknown> = {
    page: normalizedPage,
    size: params.size,
    role: normalizeRoleParam(params.role),
    status: normalizeStatusParam(params.status),
  };

  if (emailParam && emailParam.trim() !== '') {
    requestParams.email = emailParam.trim();
  }

  const response = await apiRequest<UserListApiResponse>(
    API_ENDPOINTS.USERS.LIST,
    {
      params: requestParams,
      retry: { retries: 1 },
    },
  );

  return mapUserListResponse(response, params);
};

export const createUser = async (payload: UserPayload): Promise<User> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.USERS.CREATE, {
    method: 'POST',
    data: payload,
    retry: { retries: 0 },
  });
  const user = validateUserResponse(response, {
    operation: 'create_user',
    endpoint: API_ENDPOINTS.USERS.CREATE,
  });
  return normalizeUserProfileImages(user);
};

export const updateUser = async (
  id: number,
  payload: UserPayload,
): Promise<User> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.USERS.UPDATE, {
    method: 'PUT',
    data: { id, ...payload },
    retry: { retries: 0 },
  });
  const user = validateUserResponse(response, {
    operation: 'update_user',
    endpoint: API_ENDPOINTS.USERS.UPDATE,
  });
  return normalizeUserProfileImages(user);
};

export const deleteUser = async (id: number): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.USERS.DELETE(id), {
    method: 'DELETE',
    retry: { retries: 0 },
  });
};

export const reactivateUser = async (id: number): Promise<User> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.USERS.REACTIVATE(id),
    {
      method: 'PATCH',
      retry: { retries: 0 },
    },
  );
  const user = validateUserResponse(response, {
    operation: 'reactivate_user',
    endpoint: API_ENDPOINTS.USERS.REACTIVATE(id),
  });
  return normalizeUserProfileImages(user);
};
